import React, { useState } from 'react';
import { useTournament } from '../contexts/TournamentContext';

const TournamentJoinModal = ({ isOpen, onClose, userDisplayName }) => {
  const { activeCompetitions, joinCompetition, loading } = useTournament();
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [strategyDescription, setStrategyDescription] = useState('');

  if (!isOpen) return null;

  const handleJoinCompetition = async () => {
    if (!selectedCompetition) return;

    const result = await joinCompetition(selectedCompetition.competition_id, strategyDescription);
    if (result.success) {
      onClose();
      // Reset form
      setSelectedCompetition(null);
      setStrategyDescription('');
    } else {
      alert(`Failed to join competition: ${result.error}`);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp / 1000000);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-atticus-navy-900 border border-atticus-navy-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-yellow-400">Join Tournament</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        {activeCompetitions.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">No active tournaments available</div>
            <div className="text-sm text-gray-500">Check back later for new competitions!</div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Available Competitions */}
            <div>
              <h3 className="text-gray-300 font-medium mb-3">Available Competitions</h3>
              <div className="space-y-3">
                {activeCompetitions.map((competition) => (
                  <div
                    key={competition.competition_id}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCompetition?.competition_id === competition.competition_id
                        ? 'border-yellow-400 bg-yellow-400/10'
                        : 'border-atticus-navy-700 bg-atticus-navy-800 hover:border-atticus-navy-600'
                    }`}
                    onClick={() => setSelectedCompetition(competition)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-white font-semibold mb-1">{competition.title}</h4>
                        <p className="text-gray-400 text-sm mb-2">{competition.description}</p>
                        
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>
                            <span className="text-gray-500">Type:</span>
                            <span className="text-white ml-1 capitalize">{competition.competition_type}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Starting Capital:</span>
                            <span className="text-white ml-1">{formatCurrency(competition.trading_rules.starting_capital)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Participants:</span>
                            <span className="text-white ml-1">{competition.participants.length}/{competition.entry_requirements.max_participants}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Entry Fee:</span>
                            <span className="text-white ml-1">{formatCurrency(competition.entry_requirements.entry_fee_virtual)}</span>
                          </div>
                        </div>

                        <div className="mt-3 text-xs text-gray-500">
                          <div>Start: {formatTime(competition.start_time)}</div>
                          <div>End: {formatTime(competition.end_time)}</div>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        <div className="w-3 h-3 rounded-full border-2 border-gray-400 flex items-center justify-center">
                          {selectedCompetition?.competition_id === competition.competition_id && (
                            <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Strategy Description */}
            {selectedCompetition && (
              <div>
                <h3 className="text-gray-300 font-medium mb-3">Your Strategy (Optional)</h3>
                <textarea
                  value={strategyDescription}
                  onChange={(e) => setStrategyDescription(e.target.value)}
                  placeholder="Describe your trading strategy or goals for this competition..."
                  className="w-full h-24 bg-atticus-navy-800 border border-atticus-navy-700 rounded-lg p-3 text-white placeholder-gray-500 resize-none focus:outline-none focus:border-yellow-400/50"
                />
              </div>
            )}

            {/* Competition Rules Preview */}
            {selectedCompetition && (
              <div>
                <h3 className="text-gray-300 font-medium mb-3">Competition Rules</h3>
                <div className="bg-atticus-navy-800 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Max Position Size:</span>
                    <span className="text-white">{formatCurrency(selectedCompetition.trading_rules.max_position_size)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Max Daily Loss:</span>
                    <span className="text-white">{formatCurrency(selectedCompetition.trading_rules.risk_limits.max_daily_loss)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Scoring Method:</span>
                    <span className="text-white capitalize">{selectedCompetition.trading_rules.scoring_method.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Allowed Strategies:</span>
                    <span className="text-white">{selectedCompetition.trading_rules.allowed_strategies.join(', ')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Join Button */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-atticus-navy-800 text-gray-400 rounded-lg hover:bg-atticus-navy-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleJoinCompetition}
                disabled={!selectedCompetition || loading}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-semibold rounded-lg hover:from-yellow-300 hover:to-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Joining...' : 'Join Competition'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentJoinModal; 